import React from 'react';
import { Calendar } from 'lucide-react';
import statsExplorerService, { StatsPlayerMatch } from '../../services/statsExplorerService';

const TEAL = '#3fced1';

export const WinLossBadge: React.FC<{ won: boolean | null; small?: boolean }> = ({
  won,
  small = false,
}) => {
  const sizeCls = small ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs';
  if (won === null) {
    return (
      <div
        className={`${sizeCls} rounded-full bg-zinc-800 text-gray-500 font-bold flex items-center justify-center flex-shrink-0`}
        title="Unbekannt"
      >
        ?
      </div>
    );
  }
  return (
    <div
      className={`${sizeCls} rounded-full font-bold flex items-center justify-center flex-shrink-0 ${
        won ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
      }`}
      title={won ? 'Sieg' : 'Niederlage'}
    >
      {won ? 'S' : 'N'}
    </div>
  );
};

interface PlayerMatchRowProps {
  match: StatsPlayerMatch;
  /** Name des Spielers, aus dessen Sicht das Match angezeigt wird */
  playerName?: string | null;
  /** Kurzname des Spielers (z.B. "Littler L.") für robusteres Matching */
  playerShortName?: string | null;
  /** true = kommendes Spiel (keine W/L-Anzeige, kein Score) */
  isFixture?: boolean;
}

/**
 * Eine Ergebnis-/Fixture-Zeile aus Sicht eines Spielers:
 * Datum, Turnier, Runde, Gegner, Ergebnis, W/L-Badge.
 */
const PlayerMatchRow: React.FC<PlayerMatchRowProps> = ({
  match,
  playerName,
  playerShortName,
  isFixture = false,
}) => {
  const won = isFixture ? null : statsExplorerService.didPlayerWin(match, playerName, playerShortName);
  const opponent = statsExplorerService.getOpponent(match, playerName, playerShortName);
  const opponentName = opponent
    ? statsExplorerService.cleanPlayerName(opponent.name)
    : null;
  const opponentFlag = statsExplorerService.getFlagEmoji(opponent?.countryName);

  // Score aus Sicht des Spielers (eigener zuerst)
  let scoreDisplay: string | null = null;
  const homeScore = match.home?.score;
  const awayScore = match.away?.score;
  if (typeof homeScore === 'number' && typeof awayScore === 'number') {
    if (opponent === match.away) {
      scoreDisplay = `${homeScore}:${awayScore}`;
    } else if (opponent === match.home) {
      scoreDisplay = `${awayScore}:${homeScore}`;
    } else {
      scoreDisplay = `${homeScore}:${awayScore}`;
    }
  }

  return (
    <li className="px-4 py-3 flex items-center gap-3 hover:bg-zinc-800/30 transition-colors">
      {!isFixture && <WinLossBadge won={won} />}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          {opponentName ? (
            <p className="text-sm font-medium text-white truncate">
              vs. {opponentFlag && <span className="mr-1">{opponentFlag}</span>}
              {opponentName}
            </p>
          ) : (
            <p className="text-sm font-medium text-white truncate">
              {statsExplorerService.cleanPlayerName(match.home?.name)} vs.{' '}
              {statsExplorerService.cleanPlayerName(match.away?.name)}
            </p>
          )}
          {match.round && match.round !== 'Unknown' && (
            <span className="text-xs text-gray-500">{match.round}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            {isFixture
              ? statsExplorerService.formatDateTime(match.startTime)
              : statsExplorerService.formatDate(match.startTime)}
          </span>
          {match.tournamentName && (
            <span className="truncate" style={{ color: `${TEAL}cc` }}>
              {match.tournamentName}
            </span>
          )}
        </div>
      </div>
      {!isFixture && scoreDisplay && (
        <span className="text-base font-bold tabular-nums flex-shrink-0">{scoreDisplay}</span>
      )}
      {isFixture && (
        <span
          className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full flex-shrink-0"
          style={{ backgroundColor: `${TEAL}1f`, color: TEAL }}
        >
          Geplant
        </span>
      )}
    </li>
  );
};

export default PlayerMatchRow;
